import { MShopUtilF } from '@modules/MShop/Util/F/Function/module/MShopUtilF';
import { generateProductListUrl } from '@services/MarppleShop/features/ProductList/util';
import { HeaderAbstract } from '@services/MarppleShop/renderApp/components/cells/Header/Base/HeaderAbstract.ts';
import { initHeadroom } from '@services/MarppleShop/renderApp/components/cells/Header/libs/helpers/headroom.ts';
import {
  HeaderData,
  HeaderOptions,
  HeaderProps,
  HeaderState,
} from '@services/MarppleShop/renderApp/components/cells/Header/libs/types/headerGlobal';
import { makeCartNavigate, makeScreenResetNavigate } from '@services/MarppleShop/shared/app/navigate.ts';
import { font } from '@services/MarppleShop/shared/typography';
import { typo } from '@services/MarppleShop/shared/typography/typo';
import { htmlIf } from '@services/MarppleShop/shared/util';
import { dataStr } from '@services/MarppleShop/shared/util/dataStr.ts';
import { listenDocumentEvent } from '@services/MarppleShop/shared/util/event.ts';
import { makeMainUrl } from '@services/MarppleShop/shared/util/url.ts';
import { type Html, html } from 'rune-ts';
import {
  ArrowLeftIcon,
  CartFillIcon,
  CloseIcon,
  MarppleShopTextLogo,
  SearchIcon,
  UserFillIcon,
} from '../../../atoms/Icon';
import { InputSearch } from '../../../atoms/InputSearch/InputSearch.ts';
import modal_klass from '../../../atoms/Modal/Modal.module.scss';
import { Modal } from '../../../atoms/Modal/Modal.ts';
import { SearchModule, SearchModuleCloseEvent } from '../../SearchModule/SearchModule.ts';
import {
  ShopProductCategory,
  ShopProductCategoryChangeEvent,
  ShopProductCategoryCloseClicked,
} from '../../ShopProductCategory/ShopProductCategory.ts';
import klass from './Header.module.scss';
import { HeaderHamburgerClickEvent, HeaderLeft_MshopLogoWithMenu } from './views/HeaderLeft.ts';
import { UtilityNav } from './views/UtilityNav.ts';

type makeHtml = (args: HeaderData & HeaderState) => Html;

type PcSubViews = { search_module?: SearchModule; input_search: InputSearch; utility_nav: UtilityNav };

const makeHeaderPcHtml = (args: HeaderProps & HeaderState, sub_views: PcSubViews) => {
  const { menus, transparent, theme } = args;

  const container_classes = `${klass.header} ${htmlIf(klass.transparent, transparent)} ${htmlIf(
    klass.fixed,
    args.is_fixed,
  )}`;

  const left_part =
    args.left?.whole ??
    html`<a href="${makeMainUrl(`/${ET.lang}`)}" class="${klass.left_logo_wrapper}"
      >${args.left?.logo ?? MarppleShopTextLogo}</a
    >`;

  return html` <div class="${klass.header_outer_container}">
    <div class="${container_classes}" data-theme="${theme}">
      <div class="${klass.top_menu}">${sub_views.utility_nav}</div>
      <div class="${klass.core}">
        <div class="${klass.content}">
          <div class="${klass.left_part}">${left_part}</div>

          <div class="${klass.menu_container}">
            ${menus.map(
              (menu) =>
                html`<a
                  href="${makeMainUrl(menu.url)}"
                  target="${menu.target || '_self'}"
                  class="${klass.menu} ${htmlIf(klass.current, menu.is_selected)} ${font(
                    menu.is_selected ? '16_bold' : '16_medium',
                  )}"
                >
                  ${menu.name}
                </a>`,
            )}
          </div>
          <div class="${klass.right_part}">
            <div class="${klass.search_bar_container}">${sub_views.input_search}</div>
            <a
              href="/${ET.lang}/@/creator_application?from_page=home&from_medium=quickbanner&from_content=page_creator_application"
              target="_blank"
              class="${klass.creator_application} ${font('13_bold')}"
            >
              ${ET('mps2::ad_res::creator_apply')}
            </a>
            <div class="${klass.icons}">
              <span class="${klass.search_icon}">${SearchIcon}</span>
              <a href="/${ET.lang}/@/cart" class="${klass.icon}"
                >${CartFillIcon({ count: args.cart_count })}</a
              >
              <a href="/${ET.lang}/@/mypage" class="${klass.icon}">${UserFillIcon}</a>
            </div>
          </div>
        </div>
      </div>
      <div class="${klass.sub_header_container}">${args.sub_header ?? ''}</div>
    </div>
    ${
      // search banner 는 header 밖에 있어야 함 (header 가 overflow hidden)
      sub_views.search_module
    }
  </div>`;
};

const makeHeaderMoHtml: makeHtml = (args) => {
  const { transparent, has_back_btn, has_close_btn, theme } = args;

  const container_classes = `${klass.header} ${htmlIf(klass.transparent, transparent)} ${htmlIf(
    klass.fixed,
    args.is_fixed,
  )}`;

  // 메인 파트
  const left_part =
    args.left?.whole ??
    html`<a
      href="${makeMainUrl(`/${ET.lang}`)}"
      data-post-message="${dataStr(makeScreenResetNavigate('Home'))}"
      class="${klass.left_logo_wrapper}"
      >${args.left?.logo ?? html`<div style="margin-left: 2px;">${MarppleShopTextLogo}</div>`}</a
    >`;

  const center_part = args.center?.whole ?? '';

  const right_part =
    args.right?.whole ??
    html` <div class="${klass.icons}">
      ${args.right?.off_search ? '' : html`<span class="${klass.search_icon}">${SearchIcon}</span>`}
      ${args.right?.off_cart
        ? ''
        : html`<a
            class="${klass.icon}"
            data-post-message="${dataStr(makeCartNavigate())}"
            href="/${ET.lang}/@/cart"
            >${CartFillIcon({ count: args.cart_count })}</a
          >`}
    </div>`;

  // 부분적인 추가
  const back_btn_html = htmlIf(
    html`<span class="${klass.back_arrow_btn}">${ArrowLeftIcon}</span>`,
    has_back_btn,
  );

  const close_btn_html = htmlIf(html`<span class="${klass.close_btn}">${CloseIcon()}</span>`, has_close_btn);

  const cate_items = [
    { id: undefined, cate_list_id: undefined, label: ET('mps2::product::all_products') },
    ...(args.cate_lists ?? []).flatMap((cate_list) => {
      const cate_item_all = {
        id: undefined,
        label: `${cate_list.name} ${ET('mps2::product::all')}`,
        cate_list_id: cate_list.id,
      };
      const cate_items = cate_list.cate_items.map((cate_item) => ({
        id: cate_item.id,
        label: cate_item.name,
        cate_list_id: cate_list.id,
      }));
      return [cate_item_all, ...cate_items];
    }),
  ];

  const cate_lists = [
    { id: undefined, label: 'ALL' },
    ...(args.cate_lists ?? []).map((cate_list) => ({ id: cate_list.id, label: cate_list.name })),
  ];

  return html`
    <div class="${klass.header_outer_container}">
      <div class="${container_classes}" data-theme="${theme}">
        <div class="${klass.core}">
          <div class="${klass.content}">
            <div class="${klass.left_part} ${htmlIf(klass.full, !!args.left?.full_width)}">
              ${back_btn_html} ${left_part}
            </div>
            <div class="${klass.center_part} ${typo('16_bold')}">${center_part}</div>
            <div class="${klass.right_part}">${right_part} ${close_btn_html}</div>
          </div>
        </div>
        <div class="${klass.sub_header_container}">${args.sub_header ?? ''}</div>
      </div>
      ${args.right?.off_search
        ? ''
        : new Modal({
            size: 'large',
            body: new SearchModule(args.search, {
              without_visible_interaction: true,
              is_mobile: true,
              klass: klass.search_module,
              highlight: 'stroke',
            }),
            isOpen: false,
            full_width: true,
          })}
      ${args.left?.on_category && args.cate_lists.length
        ? html` <div class="${klass.mobile_category_modal}">
            ${new ShopProductCategory(
              { cate_lists, cate_items, placeholder: args.search.placeholder },
              { webviewapp: args.webviewapp },
            )}
          </div>`
        : ''}
    </div>
  `;
};

export class Header extends HeaderAbstract<HeaderProps, HeaderState> {
  protected INPUT_SEARCH_KLASS: Readonly<string> = 'header_input_search';
  search_module?: SearchModule;
  input_search: InputSearch;
  utility_nav: UtilityNav;

  static initHeadroom = initHeadroom;

  constructor(data: HeaderProps, options: HeaderOptions = {}) {
    super(
      data,
      {
        ...options,
        has_back_btn: options.has_back_btn ?? false,
        has_close_btn: options.has_close_btn ?? false,
        transparent: options.transparent ?? false,
        theme: options.theme ?? 'normal',
        headroom:
          !options.headroom || options.headroom?.use
            ? {
                use: true,
                ...(options.headroom ?? {}),
              }
            : {
                use: false,
              },
        is_fixed: options.is_fixed ?? true,
      },
      {
        back_arrow_btn: klass.back_arrow_btn,
        center_part: klass.center_part,
        header: klass.header,
        instant_bg_change: klass.instant_bg_change,
        menu: klass.menu,
        transparent: klass.transparent,
      },
    );

    this.search_module = this.data.right?.off_search
      ? undefined
      : new SearchModule(data.search, {
          klass: klass.search_module,
          is_mobile: this.getIsMobile(),
          highlight: 'fill',
        });

    this.input_search = new InputSearch({
      ...data.search,
      transparent: this.state.transparent && this.state.theme === 'normal',
      backdrop_filter: this.state.transparent,
      off_reset: true,
      klass: this.INPUT_SEARCH_KLASS,
    });

    this.utility_nav = new UtilityNav({ lang: ET.lang as 'kr' | 'en' | 'jp', is_login: data.is_login });
  }

  override template() {
    const args = { ...this.data, ...this.state };
    return this.getIsMobile() ? makeHeaderMoHtml(args) : makeHeaderPcHtml(args, this);
  }

  override onRender() {
    const is_mobile = this.getIsMobile();

    this.delegate(HeaderHamburgerClickEvent, HeaderLeft_MshopLogoWithMenu, () => {
      const mobile_category_modal_element = this.element().querySelector(`.${klass.mobile_category_modal}`);
      if (!mobile_category_modal_element) return;
      MShopUtilF.bodyFixed$(true);
      document.body.classList.add(modal_klass.modal_fixed);
      mobile_category_modal_element.classList.add(klass.on);
    });

    this.delegate(ShopProductCategoryCloseClicked, ShopProductCategory, () => {
      const mobile_category_modal_element = this.element().querySelector(`.${klass.mobile_category_modal}`);
      if (!mobile_category_modal_element) return;
      MShopUtilF.bodyFixed$(false);
      document.body.classList.remove(modal_klass.modal_fixed);
      mobile_category_modal_element.classList.remove(klass.on);
    });

    this.delegate(ShopProductCategoryChangeEvent, ShopProductCategory, (e) => {
      const { cate_list_id, cate_item_id } = e.detail;
      location.href = generateProductListUrl({ cate_list_id, cate_item_id });
    });

    // 검색 헤더
    if (is_mobile) {
      const modalView = this.subView(Modal);
      this.delegate('click', `.${klass.search_icon}`, () => {
        if (modalView) {
          modalView.open();
        }
      });
      this.delegate(SearchModuleCloseEvent, SearchModule, () => {
        if (modalView) {
          modalView.close();
        }
      });
    } else {
      const SearchModuleView = this.subView(SearchModule);
      const openSearchModuleView = () => {
        if (SearchModuleView) {
          SearchModuleView.open();
        }
      };
      const closeSearchModuleView = () => {
        if (SearchModuleView) {
          const value = SearchModuleView.close();
          this.input_search.setValue(value);
        }
      };
      listenDocumentEvent('header_unpinned', closeSearchModuleView);
      this.delegate('focusin', InputSearch, openSearchModuleView);
      this.delegate('click', `.${klass.search_icon}`, openSearchModuleView);
      document.addEventListener('click', (e) => {
        if (!this.element().contains(e.target as HTMLElement)) {
          closeSearchModuleView();
        }
      });
    }

    this.init();
  }

  transparentEnable() {
    if (this.state.theme === 'light' || !this.input_search.isRendered()) {
      return;
    }
    this.input_search.element().classList.add(InputSearch.TRANSPARENT_KLASS);
  }

  transparentDisable() {
    if (this.state.theme === 'light' || !this.input_search.isRendered()) {
      return;
    }
    this.input_search.element().classList.remove(InputSearch.TRANSPARENT_KLASS);
  }
}
